import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { formatLocalDateYMD, parseDateOnlyToLocal } from '@shared/lib/formatLocalDate';
import { useCallback, useEffect, useState } from 'react';

/** Estado local de filtros para prefacturas (lista) */
export interface PreInvoicesFiltersState {
  status: string;
  createdDateRange: { from: Date | undefined; to: Date | undefined };
  code: string;
}

const defaultState: PreInvoicesFiltersState = {
  status: '',
  createdDateRange: { from: undefined, to: undefined },
  code: '',
};

function cloneDefaultState(): PreInvoicesFiltersState {
  return {
    ...defaultState,
    createdDateRange: { ...defaultState.createdDateRange },
  };
}

function parseConditions(conditions: FilterConditionOutput[]): PreInvoicesFiltersState {
  const state = cloneDefaultState();

  for (const c of conditions) {
    if (c.column === 'status' && (c.action === 'equals' || c.action === 'in') && c.value != null) {
      const val = c.value;
      state.status = Array.isArray(val) ? String(val[0] ?? '') : String(val);
    } else if (c.column === 'created_at') {
      if (c.action === 'greater_equal' && c.value) {
        state.createdDateRange.from = parseDateOnlyToLocal(c.value);
      } else if (c.action === 'less_equal' && c.value) {
        state.createdDateRange.to = parseDateOnlyToLocal(c.value);
      }
    } else if (c.column === 'code' && (c.action === 'equals' || c.action === 'in') && c.value != null) {
      const val = c.value;
      state.code = Array.isArray(val) ? String(val[0] ?? '') : String(val);
    }
  }

  return state;
}

function convertToConditions(state: PreInvoicesFiltersState): FilterConditionOutput[] {
  const conditions: FilterConditionOutput[] = [];

  if (state.status) {
    conditions.push({
      column: 'status',
      action: 'in',
      value: [state.status],
    });
  }
  if (state.createdDateRange.from) {
    conditions.push({
      column: 'created_at',
      action: 'greater_equal',
      value: formatLocalDateYMD(state.createdDateRange.from),
    });
  }
  if (state.createdDateRange.to) {
    conditions.push({
      column: 'created_at',
      action: 'less_equal',
      value: formatLocalDateYMD(state.createdDateRange.to),
    });
  }
  if (state.code.trim()) {
    conditions.push({
      column: 'code',
      action: 'in',
      value: [state.code.trim()],
    });
  }

  return conditions;
}

export interface UsePreInvoicesFiltersParams {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

export const usePreInvoicesFilters = ({ initialConditions = [], onChange }: UsePreInvoicesFiltersParams) => {
  const [filters, setFilters] = useState<PreInvoicesFiltersState>(() => parseConditions(initialConditions));

  useEffect(() => {
    setFilters(parseConditions(initialConditions));
  }, [initialConditions]);

  const notifyChange = useCallback(
    (next: PreInvoicesFiltersState) => {
      onChange?.(convertToConditions(next));
    },
    [onChange],
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, status: value };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleCreatedDateRangeChange = useCallback(
    (range: { from: Date | undefined; to: Date | undefined }) => {
      setFilters((prev) => {
        const next = { ...prev, createdDateRange: range };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleCodeChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, code: value };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const clearAllFilters = useCallback(() => {
    const empty = cloneDefaultState();
    setFilters(empty);
    notifyChange(empty);
  }, [notifyChange]);

  return {
    filters,
    handleStatusChange,
    handleCreatedDateRangeChange,
    handleCodeChange,
    clearAllFilters,
  };
};
